/**
 * capture-affordance.ts (BUI-95, M-DESKTOP-CAP) — pointer-aware photo/video
 * ingress affordance.
 *
 * The `capture="environment"` attribute on a file input tells a MOBILE browser
 * to open the rear camera directly instead of the OS file chooser. That is
 * exactly right for a superintendent walking a site with a phone — but on a
 * DESKTOP (mouse + keyboard, no camera worth speaking of) it either does
 * nothing useful or, on some Chromium builds, forces a webcam capture surface
 * where the user actually wants to pick an existing file from disk. The desktop
 * capture flow (Seid, 2026-07-30) calls for a file-PICKER on desktop, not a
 * camera.
 *
 * So the attribute is now a function of the pointing device, not a hardcoded
 * literal: keep `capture="environment"` where the primary pointer is COARSE and
 * there is no HOVER (a touchscreen — phone/tablet), drop it (→ `undefined`,
 * which yields the OS file picker) where the pointer is FINE and hover exists
 * (a mouse — desktop/laptop). This is the same signal the CSS `@media
 * (pointer: coarse)` / `(hover: none)` breakpoints key off, kept in one place
 * so markup and styles cannot disagree.
 *
 * Fails SAFE to the shipped mobile behaviour: if `matchMedia` is missing or
 * throws (old browser, SSR, a test env with no media engine), we return
 * `"environment"` — the pre-BUI-95 behaviour — so we never accidentally strip
 * the camera from a field phone.
 */

/** The value for a file input's `capture` attribute. `undefined` means "omit
 * the attribute", which is what yields the OS file picker on desktop. */
export type CaptureAttr = "environment" | undefined;

/** Injectable for tests — the real browser's `matchMedia`, or a stub. */
export type MatchMedia = (query: string) => { matches: boolean };

function defaultMatchMedia(): MatchMedia | null {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return null;
  }
  return (q: string) => window.matchMedia(q);
}

/**
 * True when the primary pointer is a mouse-class device (fine pointer, hover
 * available) — i.e. a desktop/laptop where the user wants a file picker rather
 * than a camera. A touchscreen (coarse pointer, no hover) returns false.
 *
 * Uses BOTH `(pointer: fine)` and `(hover: hover)` and requires both: a phone
 * with a stylus reports `(pointer: fine)` yet is still a hand-held camera
 * device, and requiring hover keeps it on the camera path. A desktop satisfies
 * both.
 */
export function prefersFilePicker(mm: MatchMedia | null = defaultMatchMedia()): boolean {
  if (!mm) return false; // fail safe: unknown → treat as mobile → keep camera
  try {
    return mm("(pointer: fine)").matches && mm("(hover: hover)").matches;
  } catch {
    return false; // fail safe
  }
}

/**
 * The `capture` attribute to place on a photo/video file input for the current
 * device: `undefined` (file picker) on desktop, `"environment"` (rear camera)
 * on a touchscreen. Evaluated at render time so a device whose pointer changes
 * (a 2-in-1 docking/undocking) gets the right affordance on the next capture.
 */
export function captureAttr(mm: MatchMedia | null = defaultMatchMedia()): CaptureAttr {
  return prefersFilePicker(mm) ? undefined : "environment";
}
